import { Link } from 'react-router';
import { Check, Navigation, Plus } from 'lucide-react';
import type { PlaceSummary } from '../lib/types';
import { hoursText, miles, openOn, place as town, today } from '../lib/format';
import { driveTo } from '../mobile/drive';
import { useTrip, type AddAs } from '../state/trip';
import KindChip from './KindChip';
import { ScoreBadge } from './ui';

interface Props {
  place: PlaceSummary;
  /** Extra line under the name, e.g. "Mile 312 · 2.4 mi off route". */
  meta?: string;
  day?: number;
  selected?: boolean;
  onSelect?: () => void;
  compact?: boolean;
  /** Adding from here also sets the trip's kind (the planner's tabs). */
  addAs?: AddAs;
  /** Show a Drive there button (Google Maps directions to this place). */
  drive?: boolean;
}

export default function PlaceCard({ place, meta, day = today(), selected, onSelect, compact, addAs, drive }: Props) {
  const { has, toggle } = useTrip();
  const inTrip = has(place.id);
  const open = openOn(place, day);

  return (
    <article
      className={`card flex flex-col gap-2 p-4 transition-colors ${selected ? 'border-amber' : 'hover:border-line-2'}`}
      onMouseEnter={onSelect}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[12px] font-bold uppercase tracking-[0.1em] text-amber">{town(place) || 'Unknown town'}</p>
          <h3 className="mt-0.5 font-sans text-lg font-bold normal-case leading-snug tracking-normal">
            <Link to={`/stops/${place.slug}`} className="text-cream hover:text-amber-hi">
              {place.name}
            </Link>
          </h3>
          {meta && <p className="text-sm text-muted tabular-nums">{meta}</p>}
          {place.distanceMiles !== null && !meta && (
            <p className="text-sm text-muted tabular-nums">{miles(place.distanceMiles)} away</p>
          )}
        </div>
        <ScoreBadge score={place.backroadScore} curated={place.isCurated} />
      </div>

      {!compact && place.note && <p className="text-[15px] text-[#d6d2c2]">{place.note}</p>}

      <div className="flex flex-wrap gap-1.5">
        <KindChip kind={place.kind} />
        <span className={`chip ${open === false ? 'chip-warn' : open ? 'chip-ok' : ''}`}>{hoursText(place, day)}</span>
        {place.warning && <span className="chip chip-warn">{place.warning}</span>}
        {!compact && place.tags.map((t) => (
          <span key={t} className={`chip ${t === 'Cash only' ? 'chip-warn' : ''}`}>
            {t}
          </span>
        ))}
      </div>

      <div className="mt-auto flex flex-wrap gap-2 pt-1">
        <button
          type="button"
          className={`btn btn-sm ${inTrip ? '' : 'btn-primary'}`}
          onClick={() => toggle(place, addAs)}
          aria-pressed={inTrip}
        >
          {inTrip ? <Check size={16} aria-hidden /> : <Plus size={16} aria-hidden />}
          {inTrip ? 'In my trip' : 'Add to trip'}
        </button>
        <Link to={`/stops/${place.slug}`} className="btn btn-sm no-underline">
          Details
        </Link>
        {drive && (
          <button type="button" className="btn btn-sm" onClick={() => void driveTo(place)}>
            <Navigation size={16} aria-hidden /> Drive there
          </button>
        )}
      </div>
    </article>
  );
}
